/**
 * @file Enhanced hook for detecting mobile and tablet viewports
 * @description 
 * This hook provides improved device detection based on screen size.
 * It supports multiple breakpoints to detect different device types.
 * 
 * Key features:
 * - Detects mobile vs desktop viewports
 * - Returns a boolean indicating if the current viewport is mobile
 * - Uses standardized breakpoints from Tailwind config
 * - Updates on window resize
 * - Works on client-side only with SSR safety
 * 
 * @dependencies
 * - React.useEffect: For window resize event handling
 * - React.useState: For managing viewport state
 */

import * as React from "react"

// Breakpoints should match with tailwind.config.ts
export const BREAKPOINTS = {
  xs: 480,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536
};

/**
 * Hook to detect if the current viewport is mobile
 * @param breakpoint - Optional custom breakpoint (default: md)
 * @returns Boolean indicating if the current viewport is mobile
 */
export function useIsMobile(breakpoint: keyof typeof BREAKPOINTS = 'md') {
  // Default to undefined for SSR compatibility
  const [isMobile, setIsMobile] = React.useState<boolean | undefined>(undefined)

  React.useEffect(() => {
    // Function to check if the viewport is mobile based on width
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < BREAKPOINTS[breakpoint])
    }
    
    // Create a media query listener for the breakpoint
    const mql = window.matchMedia(`(max-width: ${BREAKPOINTS[breakpoint] - 1}px)`)
    
    // Handler function for media query changes
    const handleMediaChange = () => checkIsMobile()
    
    // Add event listener for viewport changes
    mql.addEventListener("change", handleMediaChange)
    
    // Initial check
    checkIsMobile()
    
    // Cleanup function to remove event listener
    return () => mql.removeEventListener("change", handleMediaChange)
  }, [breakpoint]) // Re-run if breakpoint changes

  return !!isMobile
}

/**
 * Hook to detect if the current viewport is tablet
 * Lower bound is 'sm' breakpoint, upper bound is 'lg' breakpoint
 * @returns Boolean indicating if the current viewport is tablet sized
 */
export function useIsTablet() {
  // Default to undefined for SSR compatibility
  const [isTablet, setIsTablet] = React.useState<boolean | undefined>(undefined)

  React.useEffect(() => {
    // Function to check if the viewport is tablet based on width
    const checkIsTablet = () => {
      setIsTablet(
        window.innerWidth >= BREAKPOINTS.sm && 
        window.innerWidth < BREAKPOINTS.lg
      )
    }
    
    // Create media query listeners for the breakpoints
    const lowerMql = window.matchMedia(`(min-width: ${BREAKPOINTS.sm}px)`)
    const upperMql = window.matchMedia(`(max-width: ${BREAKPOINTS.lg - 1}px)`)
    
    // Handler function for media query changes
    const handleMediaChange = () => checkIsTablet()
    
    // Add event listeners
    lowerMql.addEventListener("change", handleMediaChange)
    upperMql.addEventListener("change", handleMediaChange)
    
    // Initial check
    checkIsTablet()
    
    // Cleanup function
    return () => {
      lowerMql.removeEventListener("change", handleMediaChange)
      upperMql.removeEventListener("change", handleMediaChange)
    }
  }, [])

  return !!isTablet
}

/**
 * Hook to detect if device supports hover
 * Useful for determining if device has a mouse/trackpad
 * @returns Boolean indicating if hover is supported
 */
export function useSupportsHover() {
  const [supportsHover, setSupportsHover] = React.useState<boolean | undefined>(undefined)

  React.useEffect(() => {
    // Check if the device supports hover
    const mql = window.matchMedia('(hover: hover)')
    setSupportsHover(mql.matches)

    const handleChange = (e: MediaQueryListEvent) => {
      setSupportsHover(e.matches)
    }

    mql.addEventListener('change', handleChange)
    return () => mql.removeEventListener('change', handleChange)
  }, [])

  return supportsHover
}